import type { TileMatrix } from "src/domains/shared/api/gisLayer/gisLayer.model";
import type { TileRange } from "src/domains/shared/react-cesium/utils/tiling/webMercatorTiles";
import {
  createTmsTileComposer,
  type TileImage,
  type TmsTileComposer,
} from "./tmsTileComposer";
import {
  FETCH_CONCURRENCY,
  type TileOccupancy,
  type TileXY,
} from "./tmsTileSource";
import { closeTileImage } from "./tmsTileWorkerProtocol";

export {
  buildGeotiffTilePrefix,
  buildTmsTileUrl,
  SOURCE_TILE_PX,
} from "./tmsTileSource";

export interface TmsTileIndexConfig {
  baseUrl: string;
  projectId: number;
  layerName: string;
  matrix: TileMatrix;
  /** 생략하면 환경에 맞는 합성기(Worker, 없으면 메인 스레드) — 테스트는 fake 를 넣는다 */
  composer?: TmsTileComposer;
}

/**
 * 점유 모델(`masks`/`knownEmpty`)과 그 판정(`mayHaveData`)은 메인 스레드에 있다 — 가시 타일 계산이 동기로 읽는다.
 * 타일을 실제로 받아 마스크를 내고 합성하는 일은 합성기(Worker)가 하고, 돌아온 점유 결과로 이 모델을 갱신한다.
 */
export interface TmsTileIndex {
  lowestLevel: number;
  mayHaveData(level: number, x: number, y: number): boolean;
  ensureAncestorMasks(level: number, range: TileRange): Promise<void>;
  /**
   * 소스 범위를 리프 이미지로 합성한다 — 조상 마스크를 먼저 확보하고, 데이터가 있을 수 있는 타일만 합성기에 넘긴다.
   * 받은 타일의 점유 결과는 점유 모델에 기록되어 이후 조회를 바꾼다. 그릴 타일이 없으면 null.
   */
  composeLeaf(sourceLevel: number, range: TileRange): Promise<TileImage | null>;
  dispose(): void;
}

function matrixLevelsOf(matrix: TileMatrix): number[] {
  return Object.keys(matrix)
    .map(Number)
    .filter((level) => !Number.isNaN(level));
}

export function createTmsTileIndex(config: TmsTileIndexConfig): TmsTileIndex {
  const composer =
    config.composer ??
    createTmsTileComposer({
      baseUrl: config.baseUrl,
      projectId: config.projectId,
      layerName: config.layerName,
      concurrency: FETCH_CONCURRENCY,
    });
  const masks = new Map<string, number>();
  const knownEmpty = new Set<string>();
  const probesInFlight = new Map<string, Promise<void>>();
  let cancelled = false;

  const levels = matrixLevelsOf(config.matrix);
  const lowestLevel = levels.length > 0 ? Math.min(...levels) : 0;

  const keyOf = (level: number, x: number, y: number) => `${level}/${x}/${y}`;

  const boundAt = (level: number): TileRange | null => {
    const direct = config.matrix[level];
    if (direct) return direct;
    const shift = level - lowestLevel;
    const lowest = config.matrix[lowestLevel];
    if (shift < 0 || !lowest) return null;
    return {
      minX: lowest.minX << shift,
      maxX: ((lowest.maxX + 1) << shift) - 1,
      minY: lowest.minY << shift,
      maxY: ((lowest.maxY + 1) << shift) - 1,
    };
  };

  const isWithinMatrix = (level: number, x: number, y: number): boolean => {
    const bound = boundAt(level);
    if (!bound) return false;
    return (
      x >= bound.minX && x <= bound.maxX && y >= bound.minY && y <= bound.maxY
    );
  };

  const isKnownEmpty = (level: number, x: number, y: number): boolean => {
    const key = keyOf(level, x, y);
    if (knownEmpty.has(key)) return true;
    if (level <= lowestLevel) return false;
    const parentLevel = level - 1;
    const parentX = x >> 1;
    const parentY = y >> 1;
    if (isKnownEmpty(parentLevel, parentX, parentY)) {
      knownEmpty.add(key);
      return true;
    }
    const parentMask = masks.get(keyOf(parentLevel, parentX, parentY));
    if (parentMask === undefined) return false;
    const quadrantBit = 1 << (((y & 1) << 1) | (x & 1));
    if ((parentMask & quadrantBit) === 0) {
      knownEmpty.add(key);
      return true;
    }
    return false;
  };

  const mayHaveData = (level: number, x: number, y: number): boolean =>
    isWithinMatrix(level, x, y) && !isKnownEmpty(level, x, y);

  /**
   * 점유 결과를 모델에 기록한다 — 단순 메모이제이션이 아니라 자식 타일의 `mayHaveData` 관찰 결과를 바꾸는
   * 상태 변경이다. 서버가 주지 않은 타일(mask null)과 전부 투명한 타일(mask 0)은 빈 타일로 표시된다.
   */
  const applyOccupancy = ({ level, x, y, mask }: TileOccupancy) => {
    const key = keyOf(level, x, y);
    if (mask === null) {
      knownEmpty.add(key);
      return;
    }
    if (masks.has(key)) return;
    masks.set(key, mask);
    if (mask === 0) knownEmpty.add(key);
  };

  const probeMask = (level: number, x: number, y: number): Promise<void> => {
    const key = keyOf(level, x, y);
    if (masks.has(key) || !mayHaveData(level, x, y)) return Promise.resolve();
    const inFlight = probesInFlight.get(key);
    if (inFlight) return inFlight;
    const task = composer.probe(level, x, y).then((occupancy) => {
      probesInFlight.delete(key);
      if (occupancy && !cancelled) applyOccupancy(occupancy);
    });
    probesInFlight.set(key, task);
    return task;
  };

  const ensureAncestorMasks = async (
    level: number,
    range: TileRange
  ): Promise<void> => {
    for (
      let ancestorLevel = lowestLevel;
      ancestorLevel < level && !cancelled;
      ancestorLevel++
    ) {
      const shift = level - ancestorLevel;
      const probes: Promise<void>[] = [];
      for (let y = range.minY >> shift; y <= range.maxY >> shift; y++)
        for (let x = range.minX >> shift; x <= range.maxX >> shift; x++)
          probes.push(probeMask(ancestorLevel, x, y));
      await Promise.all(probes);
    }
  };

  const composeLeaf = async (
    sourceLevel: number,
    range: TileRange
  ): Promise<TileImage | null> => {
    await ensureAncestorMasks(sourceLevel, range);
    if (cancelled) return null;
    const candidates: TileXY[] = [];
    for (let y = range.minY; y <= range.maxY; y++)
      for (let x = range.minX; x <= range.maxX; x++)
        if (mayHaveData(sourceLevel, x, y)) candidates.push({ x, y });
    if (candidates.length === 0) return null;
    const { image, occupancy } = await composer.compose(
      sourceLevel,
      range,
      candidates
    );
    if (cancelled) {
      closeTileImage(image);
      return null;
    }
    occupancy.forEach(applyOccupancy);
    return image;
  };

  const dispose = () => {
    cancelled = true;
    composer.dispose();
    masks.clear();
    knownEmpty.clear();
    probesInFlight.clear();
  };

  return {
    lowestLevel,
    mayHaveData,
    ensureAncestorMasks,
    composeLeaf,
    dispose,
  };
}
